import React from 'react';
import type { FC } from 'react';
import { css } from '@compiled/react';

import { Card } from '@atlaskit/smart-card/card/lazy';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import type { Meeting } from './meetingTypes.ts';

const embedWrapperStyle = css({
	// Give the embed a 16:9 video area to fill. `inheritDimensions` on the Card
	// lifts the iframe's built-in aspect/height restrictions, but the parent must
	// supply the dimensions (and override `.loader-wrapper`) for the embed to grow
	// into it — otherwise it collapses to a small default.
	width: '100%',
	aspectRatio: '16 / 9',
	// Prevent a stray vertical scrollbar when the iframe is a hair taller than
	// its wrapper.
	overflow: 'hidden',
	// The embed Card renders its iframe inside a `div.loader-wrapper`; make it
	// fill the 16:9 area (both dimensions) so the video is large and not
	// letterboxed. Same pattern as confluence-embeds' EmbeddedLinkView.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div.loader-wrapper': {
		height: '100%',
		width: '100%',
	},
});

type MeetingRecordingModalProps = {
	/** The meeting whose recording to show, or null when the modal is closed. */
	meeting: Meeting | null;
	onClose: () => void;
};

/**
 * Plays a meeting's Loom recording in a modal using the smart-link embed card.
 *
 * `<Card appearance="embed">` resolves the Loom URL via the ambient
 * `SmartCardProvider` (mounted by `MeetingsPanelWithProvider`) and renders the
 * playable Loom embed — the same embed shown for Loom links inside a Confluence
 * document. No manual link resolution or action harvesting required.
 */
export const MeetingRecordingModal: FC<MeetingRecordingModalProps> = ({ meeting, onClose }) => (
	<ModalTransition>
		{meeting ? (
			<Modal onClose={onClose} width={1200} testId="meeting-recording-modal">
				<ModalHeader hasCloseButton>
					<ModalTitle>{meeting.title}</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{/*
					 * The embed Card resolves the Loom URL over the network and renders
					 * an iframe — a risky subtree. Contain any failure to the modal so a
					 * broken recording never blanks the MeetingsPanel behind it.
					 */}
					<TransparentErrorBoundary attribution={Attribution.SMARTS}>
						<div css={embedWrapperStyle}>
							<Card appearance="embed" url={meeting.videoUrl} inheritDimensions />
						</div>
					</TransparentErrorBoundary>
				</ModalBody>
			</Modal>
		) : null}
	</ModalTransition>
);
